import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, FileText, Send, Trash2, Tag, Eye, Lock } from "lucide-react";
import api, { API, authToken } from "../../lib/api";
import { Modal } from "../../components/Modal";
import { formatApiErrorDetail } from "../../lib/format";

function UploadModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", tags: "", visibility: "assigned-only", page_count: 1 });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!file) return toast.error("Choose a PDF to upload.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("tags", form.tags);
      fd.append("visibility", form.visibility);
      fd.append("page_count", String(form.page_count));
      fd.append("file", file);
      await api.post("/sides", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Side uploaded.");
      onSaved();
      onClose();
      setForm({ title: "", tags: "", visibility: "assigned-only", page_count: 1 });
      setFile(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Upload sides" testid="upload-side-modal"
      footer={<>
        <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-brass text-sm" data-testid="upload-side-submit">{busy ? "Uploading…" : "Upload"}</button>
      </>}>
      <div className="space-y-4">
        <div>
          <label className="field-label">Title</label>
          <input className="field-input" value={form.title} onChange={set("title")} placeholder="The Kitchen" data-testid="side-title" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Page count</label>
            <input type="number" className="field-input" value={form.page_count} onChange={set("page_count")} data-testid="side-pages" />
          </div>
          <div>
            <label className="field-label">Visibility</label>
            <select className="field-input" value={form.visibility} onChange={set("visibility")} data-testid="side-visibility">
              <option value="assigned-only">Assigned only</option>
              <option value="public">Public library</option>
            </select>
          </div>
        </div>
        <div>
          <label className="field-label">Tags (comma separated)</label>
          <input className="field-input" value={form.tags} onChange={set("tags")} placeholder="scene, drama, two-hander" data-testid="side-tags" />
        </div>
        <div>
          <label className="field-label">PDF file</label>
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} className="field-input" data-testid="side-file" />
        </div>
      </div>
    </Modal>
  );
}

function AssignModal({ open, onClose, side, students, classes }) {
  const [mode, setMode] = useState("class");
  const [classId, setClassId] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("class");
      setClassId(classes[0]?.id || "");
      setSelected([]);
    }
  }, [open, classes]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = mode === "class" ? { class_id: classId } : { student_ids: selected };
      const r = await api.post(`/sides/${side.id}/assign`, payload);
      toast.success(`Assigned. ${r.data.emails_sent} email(s) sent.`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!side) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Assign "${side.title}"`} testid="assign-side-modal"
      footer={<>
        <button onClick={onClose} className="btn-ghost text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-brass text-sm" data-testid="assign-submit"><Send size={14} /> {busy ? "Sending…" : "Assign & notify"}</button>
      </>}>
      <div className="flex gap-2 mb-5">
        <button onClick={() => setMode("class")} className={`px-4 py-2 text-sm border ${mode === "class" ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid="assign-mode-class">Whole class</button>
        <button onClick={() => setMode("students")} className={`px-4 py-2 text-sm border ${mode === "students" ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid="assign-mode-students">Specific students</button>
      </div>
      {mode === "class" ? (
        <div>
          <label className="field-label">Class roster</label>
          <select className="field-input" value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="assign-class-select">
            {classes.map((c) => <option key={c.id} value={c.id}>{c.title} — {c.confirmed_count} cast</option>)}
          </select>
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto border border-hairline divide-y divide-hairline">
          {students.map((s) => (
            <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-hairline/30" data-testid={`assign-student-${s.id}`}>
              <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
              <span className="font-sans text-sm text-ink">{s.first_name} {s.last_name}</span>
              <span className="font-mono text-xs text-stone ml-auto">{s.email || "no email"}</span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function ReefSides() {
  const [sides, setSides] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [assignSide, setAssignSide] = useState(null);

  const load = useCallback(async () => {
    const [s, st, c] = await Promise.all([api.get("/sides"), api.get("/students"), api.get("/classes")]);
    setSides(s.data);
    setStudents(st.data);
    setClasses(c.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/sides/${id}`);
      toast.success("Side removed.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="reef-sides">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline mb-2">Scenes & scripts</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Sides Library</h1>
        </div>
        <button onClick={() => setUploadOpen(true)} className="btn-brass text-sm" data-testid="upload-side-btn">
          <Plus size={16} /> Upload sides
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {sides.map((s) => (
          <div key={s.id} className="paper-card p-6 flex flex-col" data-testid={`reef-side-${s.id}`}>
            <div className="flex items-start justify-between mb-3">
              <FileText size={22} className="text-brass" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-stone flex items-center gap-1">
                {s.visibility === "public" ? <><Eye size={11} /> Public</> : <><Lock size={11} /> Assigned</>}
              </span>
            </div>
            <h3 className="font-serif text-2xl text-ink leading-tight">{s.title}</h3>
            <p className="font-mono text-xs text-stone mt-1">{s.page_count} pp. · {s.assignment_count} sent</p>
            {s.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {s.tags.map((t) => <span key={t} className="font-sans text-[11px] text-stone flex items-center gap-1"><Tag size={10} /> {t}</span>)}
              </div>
            )}
            <div className="flex items-center gap-3 mt-5 pt-4 border-t border-hairline">
              <button onClick={() => setAssignSide(s)} className="btn-ghost text-brass text-sm font-semibold" data-testid={`assign-side-${s.id}`}>
                <Send size={14} /> Assign
              </button>
              <button onClick={() => window.open(`${API}/sides/${s.id}/download?auth=${authToken()}`, "_blank")} className="btn-ghost text-sm" data-testid={`view-side-${s.id}`}>
                View
              </button>
              <button onClick={() => remove(s.id)} className="btn-ghost text-sm text-garnet ml-auto" data-testid={`delete-side-${s.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {sides.length === 0 && <p className="font-sans text-sm text-stone">No sides uploaded yet.</p>}
      </div>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onSaved={load} />
      <AssignModal open={!!assignSide} onClose={() => setAssignSide(null)} side={assignSide} students={students} classes={classes} />
    </div>
  );
}
